import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, must } from "../api/client";
import { fromMultikey } from "../crypto/keys";
import { fetchDidLog } from "../did/log";
import { Qr } from "../components/Qr";
import { useWallet } from "../wallet/context";
import { keyPhrase } from "../wallet/present";
import { fetchMyDelegations } from "../wallet/fetchDelegations";
import type { LogEntry } from "../crypto/webvh";

const POLL_MS = 1500;

function guessLabel(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const what = /iPad|Tablet/i.test(ua) ? "Tablet" : /Android|iPhone|Mobile/i.test(ua) ? "Phone" : "Computer";
  return `${what}, linked ${new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

/**
 * /me/link: link a browser to an identity held on another device, with no
 * recovery phrase on the new one. Two halves, one page:
 *  - no #add: this (new) browser makes its key, shows /me/link#add=<multikey>&label=…
 *    as a link and QR, and waits until the DID's current document lists the key;
 *  - #add=…: the device that holds the identity shows what will be added and
 *    signs a DID update (POST /did/{did}/keys) adding it as #browser-n.
 * Nothing passes between the two browsers but that link; there is no relay.
 */
export function LinkPage() {
  const { hash } = useLocation();
  const add = useMemo(() => {
    const p = new URLSearchParams(hash.replace(/^#/, ""));
    return p.get("add") ? { multikey: p.get("add")!, label: p.get("label") ?? "" } : undefined;
  }, [hash]);
  return add ? <AddDevice multikey={add.multikey} label={add.label} /> : <NewBrowser />;
}

function NewBrowser() {
  const { wallet, identity, ready, refresh } = useWallet();
  const nav = useNavigate();
  const [key, setKey] = useState<{ kind: "Ed25519" | "P-256"; multikey: string }>();
  const [label, setLabel] = useState(guessLabel);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ready || identity) return;
    wallet.startLink().then(setKey, (e) => setErr((e as Error).message));
  }, [ready, identity, wallet]);

  // Wait for the other device: the rail lists this key in the DID's current document.
  useEffect(() => {
    if (!key) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.GET("/dids/by-key/{multikey}", { params: { path: { multikey: key.multikey } } });
        const did = r.data?.dids.find((d) => d.current)?.did;
        if (did && !stop) {
          await wallet.finishLink(did, await fetchDidLog(did));
          wallet.delegationFetch = await fetchMyDelegations(wallet);   // delegations follow the DID
          await refresh();
          stop = true;
          nav("/me");
          return;
        }
      } catch (e) { setErr((e as Error).message); }
      if (!stop) timer = setTimeout(tick, POLL_MS);
    };
    let timer = setTimeout(tick, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [key, wallet, refresh, nav]);

  if (ready && identity && !key) return (
    <div>
      <h1>Link this browser</h1>
      <p className="note">This browser already has an identity. <a href="/me">Go to it</a>, or forget it there first.</p>
    </div>
  );
  const url = key ? `${location.origin}/me/link#add=${key.multikey}&label=${encodeURIComponent(label.trim())}` : "";
  return (
    <div>
      <h1>Link this browser</h1>
      <p className="lead">Open this link on the device that already has your identity (scan the code, or send yourself the link). That device adds this browser to your DID; no recovery phrase is typed here.</p>
      {err && <p className="error" role="alert">{err}</p>}
      <label htmlFor="ll">Name this device (kept on your devices only)</label>
      <input id="ll" value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} data-testid="link-label" />
      {key && (
        <div className="card" style={{ marginTop: 14 }}>
          <Qr text={url} label="QR code for linking this browser" testId="link-qr" />
          <p className="mono" style={{ wordBreak: "break-all" }} data-testid="link-url">{url}</p>
          <div className="row">
            <button onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))}>{copied ? "Copied" : "Copy the link"}</button>
          </div>
          <p className="hint" data-testid="link-waiting">Waiting for your other device… This browser's key is {keyPhrase(key.kind)}
            {key.kind === "P-256" ? ": it will sign claims and vouches, but never change your identity." : ": it will also be able to change your identity."}</p>
        </div>
      )}
      <p className="hint">No other device? <a href="/me">Restore from your recovery phrase</a> instead.</p>
    </div>
  );
}

function AddDevice({ multikey, label }: { multikey: string; label: string }) {
  const { wallet, identity, ready, refresh } = useWallet();
  const [own, setOwn] = useState<boolean>();
  const [log, setLog] = useState<LogEntry[]>();
  const [added, setAdded] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const kind = useMemo(() => { try { return fromMultikey(multikey).kind; } catch { return undefined; } }, [multikey]);

  useEffect(() => { wallet.pendingLink().then((p) => setOwn(p?.multikey === multikey)); }, [wallet, multikey]);
  useEffect(() => {
    if (identity?.method === "webvh") fetchDidLog(identity.did).then(setLog, (e) => setErr((e as Error).message));
  }, [identity]);

  if (!ready || own === undefined) return null;
  if (own) return (
    <div><h1>Link a device</h1>
      <p className="note" data-testid="link-own">This is the link this browser made. Open this link on the device that already has your identity.</p></div>
  );
  if (!identity) return (
    <div><h1>Link a device</h1>
      <p className="note" data-testid="link-no-identity">This browser has no identity to add a device to. Open this link on the device that already has your identity.</p></div>
  );
  if (!kind) return <div><h1>Link a device</h1><p className="error" role="alert">That link does not carry a valid key.</p></div>;
  if (identity.method !== "webvh") return (
    <div><h1>Link a device</h1><p className="note">A did:key is a single key and cannot have devices. A did:webvh can.</p></div>
  );

  const approve = async () => {
    setBusy(true); setErr("");
    try {
      const current = await fetchDidLog(identity.did);
      const entry = await wallet.addDeviceUpdate(current, multikey, undefined, label || undefined);
      await must(api.POST("/did/{did}/keys", { params: { path: { did: identity.did } }, body: { entry: entry as any } }));
      const vm = (entry.state.verificationMethod as any[]).find((m) => m.publicKeyMultibase === multikey);
      setAdded(String(vm.id).slice(String(vm.id).lastIndexOf("#")));
      await refresh();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div>
      <h1>Link a device</h1>
      {err && <p className="error" role="alert">{err}</p>}
      {added ? (
        <div className="card" data-testid="link-added">
          <b>Added as {added}.</b> The other browser finishes on its own in a moment. You can remove it any time from <a href="/me">your identity</a>.
        </div>
      ) : (
        <div className="card" data-testid="link-confirm">
          <p style={{ marginTop: 0 }}>Add a device to your identity</p>
          <p className="mono" style={{ wordBreak: "break-all" }}>{identity.did}</p>
          <p>{label ? <><b>{label}</b>, with </> : "A device with "}{keyPhrase(kind)}.</p>
          <p className="hint" data-testid="link-update-key">{kind === "Ed25519"
            ? "It will also be able to change your identity: add and remove devices, set your name."
            : "It can sign claims and vouches, but never change your identity."}</p>
          <p className="hint">Only approve a link you made yourself on a device you hold. This signs a public update of your DID.</p>
          {log && !wallet.canUpdate(log) ? (
            <p className="note" data-testid="link-cannot">This browser cannot change your identity, so it cannot add devices. Open the link on a device that can (one with your phrase, or a linked Ed25519 device).</p>
          ) : (
            <div className="row"><button className="primary" disabled={busy || !log} onClick={approve} data-testid="link-approve">Sign and add this device</button></div>
          )}
        </div>
      )}
    </div>
  );
}
